/**
 * Modal de configuração para node de Carrossel
 * Permite configurar múltiplos cartões com imagem, texto e botões
 * 
 * RELEASE: 1.0.0 - 2026-02-08 - Novo dialog de configuração de carrossel multi-provider
 */

import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Separator } from '@/components/ui/separator';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
// ScrollArea removido - usando scroll nativo para robustez (v1.1.0)
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  FlowNode, 
  MessageCarouselConfig, 
  CarouselCardConfig, 
  CarouselButtonConfig 
} from '@/types/flows';
import { MediaGalleryItem } from '@/types/media';
import { toast } from 'sonner';
import { 
  HelpCircle, 
  Plus, 
  Trash2, 
  ChevronDown, 
  ChevronUp, 
  Image, 
  Link, 
  Send,
  MessageSquare,
  Phone,
  ExternalLink,
  Reply,
  GripVertical,
  LayoutGrid,
  AlertCircle
} from 'lucide-react';
import { NodeHelpDialog } from './NodeHelpDialog';
import { NodeIdField, NodeIdFieldRef } from '../NodeIdField';
import { TextareaWithVariables } from '../TextareaWithVariables';
import { MediaUrlInput } from '@/components/ui/media-url-input';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

// Logger para debug
const logger = createLogger('ConfigureCarouselDialog');

// Limites do carrossel por provider
const LIMITS = {
  // Limites Z-API (padrão)
  ZAPI: {
    MIN_CARDS: 1,
    MAX_CARDS: 10,
    MAX_BUTTONS_PER_CARD: 3,
    MAX_CARD_TEXT_LENGTH: 1024,
    MAX_BUTTON_LABEL_LENGTH: 20,
    SUPPORTS_CALL_BUTTON: true,
  },
  // Limites Meta WABA (conforme documentação oficial)
  META_WABA: {
    MIN_CARDS: 2,
    MAX_CARDS: 10,
    MAX_BUTTONS_PER_CARD: 2, // 1 URL ou até 2 Quick Reply
    MAX_CARD_TEXT_LENGTH: 160,
    MAX_BUTTON_LABEL_LENGTH: 20,
    SUPPORTS_CALL_BUTTON: false,
  }
};

// Mantém compatibilidade com código existente
const MAX_CARDS = 10;
const MAX_BUTTONS_PER_CARD = 3;
const MAX_CARD_TEXT_LENGTH = 1024;
const MAX_BUTTON_LABEL_LENGTH = 20;

/**
 * Gera UUID simples para IDs
 */
function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

interface ConfigureCarouselDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  node: FlowNode | null;
  onSave: (nodeId: string, config: MessageCarouselConfig) => void;
  tenantId: string;
}

/**
 * Componente para configurar um único botão do cartão
 */
function ButtonEditor({
  button,
  onUpdate,
  onRemove,
}: {
  button: CarouselButtonConfig;
  onUpdate: (button: CarouselButtonConfig) => void;
  onRemove: () => void;
}) {
  return (
    <div className="flex items-start gap-2 p-2 border rounded-lg bg-muted/30">
      {/* Tipo do botão */}
      <Select
        value={button.type}
        onValueChange={(v: 'URL' | 'REPLY' | 'CALL') => onUpdate({ ...button, type: v })}
      >
        <SelectTrigger className="w-24">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="URL">
            <span className="flex items-center gap-1">
              <ExternalLink className="h-3 w-3" />
              URL
            </span>
          </SelectItem>
          <SelectItem value="REPLY">
            <span className="flex items-center gap-1">
              <Reply className="h-3 w-3" />
              Resposta
            </span>
          </SelectItem>
          <SelectItem value="CALL">
            <span className="flex items-center gap-1">
              <Phone className="h-3 w-3" />
              Ligar
            </span>
          </SelectItem>
        </SelectContent>
      </Select>

      {/* Label do botão */}
      <Input
        placeholder="Texto do botão"
        value={button.label}
        onChange={(e) => onUpdate({ ...button, label: e.target.value.slice(0, MAX_BUTTON_LABEL_LENGTH) })}
        className="flex-1"
        maxLength={MAX_BUTTON_LABEL_LENGTH}
      />

      {/* Campo específico por tipo */}
      {button.type === 'URL' && (
        <Input
          placeholder="https://..."
          value={button.url || ''}
          onChange={(e) => onUpdate({ ...button, url: e.target.value })}
          className="flex-1"
        />
      )}
      {button.type === 'CALL' && (
        <Input
          placeholder="5511999999999"
          value={button.phone || ''}
          onChange={(e) => onUpdate({ ...button, phone: e.target.value.replace(/\D/g, '') })}
          className="w-36"
        />
      )}

      {/* Botão remover */}
      <Button variant="ghost" size="icon" onClick={onRemove} className="text-destructive">
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
}

/**
 * Componente para configurar um único cartão
 */
function CardEditor({
  card,
  index,
  onUpdate,
  onRemove,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
  tenantId,
}: {
  card: CarouselCardConfig;
  index: number;
  onUpdate: (card: CarouselCardConfig) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  isFirst: boolean;
  isLast: boolean;
  tenantId: string;
}) {
  const [isOpen, setIsOpen] = useState(true);

  // Handler para adicionar botão
  const handleAddButton = () => {
    if ((card.buttons?.length || 0) >= MAX_BUTTONS_PER_CARD) {
      toast.warning(`Máximo de ${MAX_BUTTONS_PER_CARD} botões por cartão`);
      return;
    }
    const newButton: CarouselButtonConfig = {
      id: generateId(),
      type: 'REPLY',
      label: '',
    };
    onUpdate({
      ...card,
      buttons: [...(card.buttons || []), newButton],
    });
  };

  // Handler para atualizar botão
  const handleUpdateButton = (buttonIndex: number, updatedButton: CarouselButtonConfig) => {
    const newButtons = [...(card.buttons || [])];
    newButtons[buttonIndex] = updatedButton;
    onUpdate({ ...card, buttons: newButtons });
  };

  // Handler para remover botão
  const handleRemoveButton = (buttonIndex: number) => {
    const newButtons = (card.buttons || []).filter((_, i) => i !== buttonIndex);
    onUpdate({ ...card, buttons: newButtons });
  };

  // Handler para seleção de imagem
  const handleImageChange = (url: string) => {
    onUpdate({ ...card, image_url: url });
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="border rounded-lg overflow-hidden">
        {/* Header do cartão */}
        <CollapsibleTrigger asChild>
          <div className="flex items-center gap-2 p-3 bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors">
            <GripVertical className="h-4 w-4 text-muted-foreground" />
            
            {/* Preview da imagem em miniatura */}
            <div className="w-8 h-8 rounded bg-muted flex items-center justify-center overflow-hidden">
              {card.image_url ? (
                <img 
                  src={card.image_url} 
                  alt="" 
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              ) : (
                <Image className="h-4 w-4 text-muted-foreground" />
              )}
            </div>

            <div className="flex-1">
              <span className="font-medium text-sm">Cartão {index + 1}</span>
              {card.text && (
                <span className="text-xs text-muted-foreground ml-2 truncate">
                  {card.text.slice(0, 30)}{card.text.length > 30 ? '...' : ''}
                </span>
              )}
            </div>

            {/* Badges de info */}
            {card.buttons && card.buttons.length > 0 && (
              <Badge variant="outline" className="text-[10px]">
                {card.buttons.length} botão{card.buttons.length > 1 ? 'ões' : ''}
              </Badge>
            )}

            {/* Controles de ordem e remoção */}
            <div className="flex items-center gap-1">
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-7 w-7"
                onClick={(e) => { e.stopPropagation(); onMoveUp(); }}
                disabled={isFirst}
              >
                <ChevronUp className="h-4 w-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-7 w-7"
                onClick={(e) => { e.stopPropagation(); onMoveDown(); }}
                disabled={isLast}
              >
                <ChevronDown className="h-4 w-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-7 w-7 text-destructive"
                onClick={(e) => { e.stopPropagation(); onRemove(); }}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>

            {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </div>
        </CollapsibleTrigger>

        {/* Conteúdo do cartão */}
        <CollapsibleContent>
          <div className="p-4 space-y-4 border-t">
            {/* Imagem do cartão */}
            <div className="space-y-2">
              <Label>Imagem do Cartão *</Label>
              <MediaUrlInput
                value={card.image_url}
                onChange={(url) => handleImageChange(url || '')}
                placeholder="https://exemplo.com/imagem.jpg"
                showPreview={true}
                allowedTypes={['image']}
              />
              <p className="text-xs text-muted-foreground">
                Recomendado: 800x418px (proporção 1.91:1)
              </p>
            </div>

            {/* Texto do cartão */}
            <div className="space-y-2">
              <Label>Texto do Cartão *</Label>
              <TextareaWithVariables
                tenantId={tenantId}
                value={card.text}
                onChange={(v) => onUpdate({ ...card, text: v })}
                placeholder="Descrição do cartão (suporta {{variáveis}})"
                maxLength={MAX_CARD_TEXT_LENGTH}
                rows={3}
              />
              <p className="text-xs text-muted-foreground">
                {card.text.length}/{MAX_CARD_TEXT_LENGTH} caracteres
              </p>
            </div>

            {/* Botões do cartão */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Botões (opcional)</Label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleAddButton}
                  disabled={(card.buttons?.length || 0) >= MAX_BUTTONS_PER_CARD}
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Adicionar
                </Button>
              </div>

              {card.buttons && card.buttons.length > 0 ? (
                <div className="space-y-2">
                  {card.buttons.map((btn, btnIndex) => (
                    <ButtonEditor
                      key={btn.id}
                      button={btn}
                      onUpdate={(updated) => handleUpdateButton(btnIndex, updated)}
                      onRemove={() => handleRemoveButton(btnIndex)}
                    />
                  ))}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground italic">
                  Nenhum botão configurado
                </p>
              )}
            </div>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

/**
 * Dialog principal de configuração do carrossel
 */
export function ConfigureCarouselDialog({
  open,
  onOpenChange,
  node,
  onSave,
  tenantId,
}: ConfigureCarouselDialogProps) {
  // Estado do formulário
  const [message, setMessage] = useState('');
  const [cards, setCards] = useState<CarouselCardConfig[]>([]);
  const [showHelp, setShowHelp] = useState(false);
  /** Ref para capturar apelido do node */
  const nicknameRef = useRef<NodeIdFieldRef>(null);
  
  // Estados para canal de envio
  const [channelMode, setChannelMode] = useState<'active' | 'specific'>('active');
  const [selectedInboxId, setSelectedInboxId] = useState<string>('');

  /**
   * Query para buscar inboxes ativas do tenant (inclui provider_slug para validações)
   * RELEASE: 1.1.0 - Adiciona provider_slug para validação Meta WABA
   */
  const { data: inboxes = [], isLoading: loadingInboxes } = useQuery({
    queryKey: ['inboxes-selector', tenantId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('inboxes')
        .select(`
          id, 
          name, 
          channel_type, 
          identifier,
          provider_slug:api_providers(slug)
        `)
        .eq('tenant_id', tenantId)
        .eq('status', 'active')
        .order('name');
      
      if (error) throw error;
      
      // Mapeia para extrair o slug do provider
      return (data || []).map((inbox: any) => ({
        ...inbox,
        provider_slug: inbox.provider_slug?.slug || null
      }));
    },
    enabled: !!tenantId && open,
  });

  /**
   * Detecta se a inbox selecionada é Meta WABA (para validações específicas)
   * RELEASE: 1.1.0 - Suporte a Meta WABA com regras diferentes
   */
  const selectedInboxData = inboxes.find(i => i.id === selectedInboxId);
  const isMetaWaba = selectedInboxData?.provider_slug === 'meta-whatsapp' || 
                     selectedInboxData?.provider_slug === 'notificame';
  
  // Limites baseados no provider
  const currentLimits = isMetaWaba ? LIMITS.META_WABA : LIMITS.ZAPI;

  /**
   * Sincroniza estados quando node mudar
   */
  useEffect(() => {
    if (node) {
      const config = node.data.config as MessageCarouselConfig | undefined;
      setMessage(config?.message || '');
      setCards(config?.cards || []);
      
      // Canal de envio
      const hasInboxConfig = !!config?.inbox_id;
      setChannelMode(hasInboxConfig ? 'specific' : 'active');
      setSelectedInboxId(config?.inbox_id || '');
    }
  }, [node]);

  /**
   * Adiciona novo cartão
   */
  const handleAddCard = () => {
    if (cards.length >= MAX_CARDS) {
      toast.warning(`Máximo de ${MAX_CARDS} cartões permitidos`);
      return;
    }
    
    const newCard: CarouselCardConfig = {
      id: generateId(),
      image_url: '',
      text: '',
      buttons: [],
    };
    setCards([...cards, newCard]);
    logger.debug('Cartão adicionado', { totalCards: cards.length + 1 });
  };

  /**
   * Atualiza cartão
   */
  const handleUpdateCard = (index: number, updatedCard: CarouselCardConfig) => {
    const newCards = [...cards];
    newCards[index] = updatedCard;
    setCards(newCards);
  };

  /**
   * Remove cartão
   */
  const handleRemoveCard = (index: number) => {
    if (cards.length <= 1) {
      toast.warning('O carrossel precisa de pelo menos 1 cartão');
      return;
    }
    setCards(cards.filter((_, i) => i !== index));
    logger.debug('Cartão removido', { index, totalCards: cards.length - 1 });
  };

  /**
   * Move cartão para cima
   */
  const handleMoveCardUp = (index: number) => {
    if (index === 0) return;
    const newCards = [...cards];
    [newCards[index - 1], newCards[index]] = [newCards[index], newCards[index - 1]];
    setCards(newCards);
  };

  /**
   * Move cartão para baixo
   */
  const handleMoveCardDown = (index: number) => {
    if (index === cards.length - 1) return;
    const newCards = [...cards];
    [newCards[index], newCards[index + 1]] = [newCards[index + 1], newCards[index]];
    setCards(newCards);
  };

  /**
   * Valida e salva configuração
   */
  const handleSave = () => {
    if (!node) return;

    // Validações
    if (channelMode === 'specific' && !selectedInboxId) {
      toast.error('Selecione uma inbox para envio');
      return;
    }

    // Validação de mínimo de cartões (varia por provider)
    const minCards = isMetaWaba ? LIMITS.META_WABA.MIN_CARDS : 1;
    if (cards.length < minCards) {
      if (isMetaWaba) {
        toast.error(`Meta WABA requer mínimo de ${minCards} cartões no carrossel`);
      } else {
        toast.error('Adicione pelo menos 1 cartão ao carrossel');
      }
      return;
    }

    // Valida cada cartão (com regras específicas por provider)
    for (let i = 0; i < cards.length; i++) {
      const card = cards[i];
      if (!card.image_url) {
        toast.error(`Cartão ${i + 1}: Imagem é obrigatória`);
        return;
      }
      if (!card.text || card.text.trim() === '') {
        toast.error(`Cartão ${i + 1}: Texto é obrigatório`);
        return;
      }
      
      // Validação de texto do cartão para Meta WABA (máx 160 chars)
      if (isMetaWaba && card.text.length > LIMITS.META_WABA.MAX_CARD_TEXT_LENGTH) {
        toast.warning(`Cartão ${i + 1}: Texto será truncado para ${LIMITS.META_WABA.MAX_CARD_TEXT_LENGTH} caracteres no Meta WABA`);
      }
      
      // Valida botões
      if (card.buttons) {
        for (let j = 0; j < card.buttons.length; j++) {
          const btn = card.buttons[j];
          if (!btn.label || btn.label.trim() === '') {
            toast.error(`Cartão ${i + 1}, Botão ${j + 1}: Texto do botão é obrigatório`);
            return;
          }
          if (btn.type === 'URL' && (!btn.url || !btn.url.startsWith('http'))) {
            toast.error(`Cartão ${i + 1}, Botão ${j + 1}: URL inválida`);
            return;
          }
          if (btn.type === 'CALL' && (!btn.phone || btn.phone.length < 10)) {
            toast.error(`Cartão ${i + 1}, Botão ${j + 1}: Telefone inválido`);
            return;
          }
          // Alerta sobre botão CALL não suportado no Meta WABA
          if (isMetaWaba && btn.type === 'CALL') {
            toast.warning(`Cartão ${i + 1}, Botão ${j + 1}: Botão CALL será removido (não suportado no Meta WABA)`);
          }
        }
      }
    }

    // Monta configuração
    const selectedInbox = inboxes.find(i => i.id === selectedInboxId);
    const config: MessageCarouselConfig = {
      message: message.trim(),
      cards,
      ...(channelMode === 'specific' && selectedInboxId && {
        inbox_id: selectedInboxId,
        inbox_name: selectedInbox?.name,
      }),
    };

    logger.info('Salvando configuração do carrossel', { 
      cardCount: cards.length,
      hasMessage: !!message,
      channelMode,
    });

    onSave(node.id, { ...config, nickname: nicknameRef.current?.getNickname() } as any);
    toast.success('Configuração salva com sucesso');
    onOpenChange(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        {/* v1.1.0: Padrão robusto de scroll - altura fixa + scroll nativo (não usa ScrollArea) */}
        <DialogContent className="max-w-3xl h-[85vh] max-h-[85vh] flex flex-col overflow-hidden min-h-0">
          <DialogHeader className="shrink-0">
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2">
                <LayoutGrid className="h-5 w-5 text-amber-500" />
                Configurar Carrossel
              </DialogTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowHelp(true)}>
                <HelpCircle className="h-5 w-5" />
              </Button>
            </div>
          </DialogHeader>

          {/* Body com scroll nativo - compatível com wheel, trackpad e drag */}
          <div className="flex-1 min-h-0 overflow-y-auto pr-4">
            <div className="space-y-4 py-4">
              {/* Campo ID do Node */}
              <NodeIdField ref={nicknameRef} nodeId={node?.id} defaultNickname={node?.data.config?.nickname} />

              {/* Seção: Canal de Envio */}
              <Separator className="my-2" />
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Send className="h-4 w-4 text-muted-foreground" />
                  <Label className="text-sm font-medium">Canal de Envio</Label>
                </div>
                <RadioGroup
                  value={channelMode}
                  onValueChange={(v: 'active' | 'specific') => setChannelMode(v)}
                  className="space-y-2"
                >
                  <div className="flex items-start space-x-3 p-3 rounded-lg border hover:bg-accent/50 cursor-pointer">
                    <RadioGroupItem value="active" id="channel-active" className="mt-0.5" />
                    <div className="space-y-1 flex-1">
                      <Label htmlFor="channel-active" className="font-medium cursor-pointer">
                        Canal da conversa ativa
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Usa o canal definido pelo gatilho do fluxo
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3 p-3 rounded-lg border hover:bg-accent/50 cursor-pointer">
                    <RadioGroupItem value="specific" id="channel-specific" className="mt-0.5" />
                    <div className="space-y-1 flex-1">
                      <Label htmlFor="channel-specific" className="font-medium cursor-pointer">
                        Canal específico
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Selecione manualmente o canal (obrigatório para gatilhos CRM)
                      </p>
                    </div>
                  </div>
                </RadioGroup>

                {/* Seletor de Inbox */}
                {channelMode === 'specific' && (
                  <div className="space-y-2 pl-4 border-l-2 border-primary/30">
                    <Label>Inbox para Envio *</Label>
                    <Select 
                      value={selectedInboxId} 
                      onValueChange={setSelectedInboxId}
                      disabled={loadingInboxes}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione uma inbox..." />
                      </SelectTrigger>
                      <SelectContent>
                        {inboxes.map((inbox) => (
                          <SelectItem key={inbox.id} value={inbox.id}>
                            <div className="flex items-center gap-2">
                              <MessageSquare className="h-3 w-3" />
                              <span>{inbox.name}</span>
                              <span className="text-xs text-muted-foreground">
                                ({inbox.channel_type})
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                {/* Alerta de regras específicas para Meta WABA */}
                {isMetaWaba && channelMode === 'specific' && (
                  <Alert className="bg-warning/10 border-warning/30">
                    <AlertCircle className="h-4 w-4 text-warning" />
                    <AlertDescription className="text-warning-foreground text-xs">
                      <strong>Meta WABA:</strong> Mínimo 2 cartões obrigatórios. 
                      Todos os cartões devem ter mesmo tipo e quantidade de botões. 
                      Botões de ligação (CALL) não são suportados.
                      Texto do cartão limitado a 160 caracteres.
                    </AlertDescription>
                  </Alert>
                )}
              </div>

              <Separator className="my-2" />

              {/* Seção: Mensagem Principal */}
              <div className="space-y-2">
                <Label>Mensagem Principal (opcional)</Label>
                <TextareaWithVariables
                  tenantId={tenantId}
                  value={message}
                  onChange={setMessage}
                  placeholder="Texto exibido acima dos cartões (suporta {{variáveis}})"
                  rows={2}
                />
              </div>

              <Separator className="my-2" />

              {/* Seção: Cartões */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-medium">Cartões do Carrossel</Label>
                    <p className="text-xs text-muted-foreground">
                      {cards.length}/{MAX_CARDS} cartões
                      {isMetaWaba && cards.length < currentLimits.MIN_CARDS && (
                        <span className="text-warning ml-1">
                          (mínimo {currentLimits.MIN_CARDS} para Meta WABA)
                        </span>
                      )}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={handleAddCard}
                    disabled={cards.length >= MAX_CARDS}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Adicionar Cartão
                  </Button>
                </div>

                {cards.length > 0 ? (
                  <div className="space-y-3">
                    {cards.map((card, index) => (
                      <CardEditor
                        key={card.id}
                        card={card}
                        index={index}
                        onUpdate={(updated) => handleUpdateCard(index, updated)}
                        onRemove={() => handleRemoveCard(index)}
                        onMoveUp={() => handleMoveCardUp(index)}
                        onMoveDown={() => handleMoveCardDown(index)}
                        isFirst={index === 0}
                        isLast={index === cards.length - 1}
                        tenantId={tenantId}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="border-2 border-dashed rounded-lg p-8 text-center">
                    <LayoutGrid className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground">
                      Nenhum cartão adicionado
                    </p>
                    <Button
                      variant="outline"
                      className="mt-3"
                      onClick={handleAddCard}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Adicionar Primeiro Cartão
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>

          <DialogFooter className="shrink-0 border-t pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave}>
              Salvar Configuração
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de ajuda */}
      <NodeHelpDialog
        open={showHelp}
        onOpenChange={setShowHelp}
        nodeType="message_carousel"
      />
    </>
  );
}
